import React, { useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import styled from 'styled-components';

import PrintIcon from '@material-ui/icons/Print';
import { AxiosResponse } from 'axios';

import { useSelector } from 'react-redux';
import Table from '../organisms/Table';
import ModalInfo from '../molecules/ModalInfo';
import useTablePage from '../hooks/useTablePage';
import {
  ModalDefaultDraggable,
  LoaderWithContainer,
} from '../molecules';
import DashboardLayout from '../layouts/DashboardLayout';
import InvoicePreviewForm from '../forms/InvoicePreviewForm';
import tableColumnsFormatter from '../utils/tableColumnsFormatter';
import {
  getInvoicesList,
  InvoiceTableType,
  getPDFInvoiceByID,
  getNewInvoicesList,
} from '../api/invoices';
import ActionsPanelInvoices from '../organisms/ActionsPanelInvoices';
import { convertTableAmountToCurrency } from '../utils/convertTableValues';
import { formatDateTime } from '../utils/formatDate';
import { DATETIME_FORMAT } from '../constants';
import { getCurrentUser } from '../api/userSettings';
import useShowInvoicePdf from '../Components/InvoiceTool/useShowInvoicePdf';
import { RootState } from '../redux/root';
import { InvoiceWrapper } from '../forms/FeesSendForm';
import InvoiceTool from '../Components/InvoiceTool/InvoiceTool';
import { useGetInvoiceDetailsQuery } from '../redux/rtkQuery/apiSlice';
import ViewInvoiceModal from '../Components/InvoiceTool/ViewInvoiceModal';

const InvoicesPage = () => {
  const {
    values,
    isModalOpen,
    handleEdit,
    handleCloseModal,
    handleCloseModalInfo,
    modalErrorTitle,
    modalError,
    modalSuccess,
    modalTitle,
    // updateTable,
  } = useTablePage<InvoiceTableType>({
    modalUpdateTitle: 'Invoice',
  });

  const [
    selectedAdditionalParams,
    setSelectedAdditionalParams,
  ] = React.useState<any>(undefined);
  const info = useSelector((s:RootState) => s.userAuth.info);
  const [isLoading, setIsLoading] = React.useState<boolean>(
    false,
  );
   const [selectedFeesIds, setSelectedFeesIds] = useState<number[]>([]);
   const [invoiceId, setInvoiceId] = useState<string>('');
  const { handleInvoiceCellClick, reimbursableAttachment } = useShowInvoicePdf(selectedFeesIds);
  const { data: invoiceDetails } = useGetInvoiceDetailsQuery(invoiceId, { skip: invoiceId === '' });
  const [
    isTableLoading,
    setIsTableLoading,
  ] = React.useState<boolean>(false);

  const [franchise, setFranchise] = React.useState(null);

  React.useEffect(() => {
    // The active branch, not franchise[0] - otherwise the generated invoice PDF is
    // scoped to a different branch than the one selected in the header.
    const activeFranchise =
      info?.selectedFranchise || info?.defaultFranchiseId;

    getCurrentUser(activeFranchise).then(currentUser => {
      setFranchise(
        activeFranchise || currentUser?.data?.franchise[0]?.id,
      );
    });
  }, [info?.selectedFranchise, info?.defaultFranchiseId]);

  useEffect(() => {
    if (invoiceDetails) {
    handleInvoiceCellClick(invoiceDetails);
    }
  }, [invoiceDetails, invoiceId]);

  const columns = column => {
    if (
      [
        'balance',
        'deposit',
        'dueDate',
        'lastUpdateTime',
        'customerStripeId',
        'updatedAt',
        'paidAt',
        'invoicePdf',
        'type',
        'stripeId',
        'permitNumber',
        'projectName',
        'projectId',
        'permitId',
        'permitName',
        'fullNumber',
        'contactId',
        'invoicedAt',
        'dueAt',
        'transactionFee',
        'totalWithTransFee',
        // QBO sync columns are not needed in the admin Invoices table.
        'qboInvoiceId',
        'qboSyncStatus',
        'qboSyncedAt',
        'qboPaymentLink',
      ].includes(column)
    ) {
      return null;
    }

    switch (column) {
      case 'docNumber':
        return 'Number';

      case 'createdAt':
        return 'Date';

      case 'invoiceNumber':
        return 'Invoice Number';

      case 'isPaid':
        return 'Paid';

      case 'total':
        return 'Amount';
      case 'franchise':
          return 'Branch';
      case 'projectPermit':
          return 'Project Name | Permit Name';
      default:
        return tableColumnsFormatter(column);
    }
  };

  const columnsOrder = [
    'id',
    'projectPermit',
    'permitNumber',
    'invoiceNumber',
    'createdAt',
    'docNumber',
    'customer',
    'totalAmt',
    // 'invoiceStatus',
    'isPaid',
  ];

  const useSortBySelectedColumns = [
    'id',
    'docNumber',
    // 'createdAt',
    'isPaid',
  ];

  const initialSortBy = [
    {
      id: 'id',
      desc: true,
    },
  ];

  const dateSort2 = (self, other) => {
    if (new Date(self.createdAt) > new Date(other.createdAt)) return -1;
    if (new Date(self.createdAt) < new Date(other.createdAt)) return 1;
    return 0;
  };

  const dateSort1 = (self, other) => {
    if (new Date(self.invoicedAt) > new Date(other.invoicedAt)) return -1;
    if (new Date(self.invoicedAt) < new Date(other.invoicedAt)) return 1;
    return 0;
  };

  const modifiedGetInvoices = params => {
    // Any request carrying ?franchise_id= rewrites the shared Django session, so sending
    // the default branch here silently switched every later page back to it.
    const _params = {
      ...params,
      franchise_id: info.selectedFranchise || info.defaultFranchiseId,
    };

    return Promise.all([getInvoicesList(_params), getNewInvoicesList(_params)]).then(
      ([invoicesResponse, newInvoiceResponse]) => {
        const results1 = invoicesResponse.data.results.map(
          invoice => ({
            ...invoice,
            createdAt: formatDateTime(
              invoice.createdAt,
              DATETIME_FORMAT,
            ),
            projectPermit: `${invoice.projectName}/${invoice.permitName}`,
            invoiceNumber: invoice.invoiceNumber,
          }),
        ) || [];
        const results2 = newInvoiceResponse.data.results.map(
          invoice => {
            const invArr = invoice.invoiceNumber ? invoice.invoiceNumber.split('----') : ['', ''];
            return {
            ...invoice,
            createdAt: formatDateTime(
              invoice.invoicedAt,
              DATETIME_FORMAT,
            ),
            projectPermit: `${invoice.projectName}/${invoice.permitName}`,
            invoiceNumber: invArr[0],
            fullNumber: invoice.invoiceNumber,
          };
          },
        ) || [];
        const one = results1.sort(dateSort1);
        const two = results2.sort(dateSort2);
        return {
          ...invoicesResponse,
          data: {
            ...invoicesResponse.data,
            results: [...two, ...one],
          },
        };
      },
    );
  };

  const getInvoicePdf = invoice => {
    setIsLoading(true);
    return getPDFInvoiceByID(invoice.id)
      .then((response: AxiosResponse) => {
        const file = new Blob([response.data], {
          type: 'application/pdf',
        });

        saveAs(file, 'invoice.pdf');
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  const additionalActions = ({ invoiceStatus }) => {
    if (invoiceStatus?.toUpperCase() === 'PAID') {
      return [
        {
          title: 'Print',
          handler: getInvoicePdf,
          Icon: PrintIcon,
        },
      ];
    }

    return undefined;
  };

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  const handleCellClick = data => {
    if (data.fullNumber) {
      setInvoiceId(data.fullNumber);
      return;
    }
    handleInvoiceCellClick(data);
  };
  return (
    <DashboardLayout>
      {isLoading && <StyledLoaderWithContainer />}
      <PageWrapper>
        <ActionsPanelInvoices
          isLoading={isLoading || isTableLoading}
          onSelectedAdditionalParamsChange={
            setSelectedAdditionalParams
          }
        />

      <InvoiceWrapper>
        <InvoiceTool fees={selectedFeesIds} reimbursableAttachment={reimbursableAttachment} />
      </InvoiceWrapper>
      <ViewInvoiceModal />
      <Container>
        {selectedAdditionalParams && (
          <Table
            customSearch
            useViewModeInsteadEdit
            initialUseSortBySelectedColumns={
              useSortBySelectedColumns
            }
            columns={columns}
            rows={modifiedGetInvoices}
            columnsOrder={columnsOrder}
            formatValue={convertTableAmountToCurrency}
            additionalRequestParams={
              selectedAdditionalParams
            }
            initialSortBy={initialSortBy}
            onChangeLoadingStatus={setIsTableLoading}
            invoicePage
            onEdit={null}
            onConfigure={null}
            onDelete={null}
            additionalActions={null}
            cellCallbackFnIsOn={['invoiceNumber']}
            handleCellClick={(data, column) =>
              handleCellClick(data, column)}
          />
        )}
      </Container>

      {isModalOpen && franchise && (
        <ModalDefaultDraggable
          onClose={handleCloseForm}
          title={modalTitle}
        >
          <InvoicePreviewForm values={values} franchise={franchise} />
        </ModalDefaultDraggable>
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo
          timeToClose={5000}
          isError={modalError}
          onClose={handleCloseModalInfo}
          errorMsg={modalErrorTitle}
        />
      )}
      </PageWrapper>
    </DashboardLayout>
  );
};

const PageWrapper = styled.div`
  width: 100%;
  padding: 20px 16px 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const Container = styled.div`
  margin: 20px 0 0 0;
`;

const StyledLoaderWithContainer = styled(
  LoaderWithContainer,
)`
  background-color: rgba(255, 255, 255, 0.5);
  z-index: 30;
`;
export default InvoicesPage;
